import React, { useState } from "react";
import { cn } from "../../../lib/cn";

/**
 * Avatar — círculo con foto o iniciales en fondo coloreado.
 *
 * @example
 * <Avatar name="Iban Lopetegi" />
 * <Avatar src="/api/persona/imagen/abc.jpg" name="Iban Lopetegi" />
 * <Avatar name="Maria López" size="lg" />
 */

const sizeClasses = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  default: "h-9 w-9 text-xs",
  lg: "h-12 w-12 text-sm",
  xl: "h-16 w-16 text-base",
};

// Paleta de colores estables para avatares (basado en hash del nombre)
const colorPalette = [
  "bg-pink-100 text-pink-800",
  "bg-amber-100 text-amber-800",
  "bg-blue-100 text-blue-800",
  "bg-emerald-100 text-emerald-800",
  "bg-violet-100 text-violet-800",
  "bg-rose-100 text-rose-800",
  "bg-cyan-100 text-cyan-800",
  "bg-orange-100 text-orange-800",
  "bg-teal-100 text-teal-800",
  "bg-fuchsia-100 text-fuchsia-800",
];

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getColorClass(name = "") {
  if (!name) return "bg-muted text-muted-foreground";
  return colorPalette[hashCode(name) % colorPalette.length];
}

export const Avatar = ({ src, name = "", size = "default", className, colored = true, ...props }) => {
  const [imageError, setImageError] = useState(false);
  const showImage = src && !imageError;
  const initials = getInitials(name);
  const colorClass = colored ? getColorClass(name) : "bg-muted text-muted-foreground";

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center rounded-full font-medium overflow-hidden shrink-0",
        !showImage && colorClass,
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt={name}
          onError={() => setImageError(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};
